import {
  ActionIcon,
  Button,
  Group,
  Input,
  Menu,
  Modal,
  Stack,
  Text,
  Tooltip,
} from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import {
  IconArrowsMove,
  IconDotsVertical,
  IconExternalLink,
  IconPencil,
  IconTrash,
} from "@tabler/icons-react"
import { useMemo, useRef } from "react"
import { Link, useSearchParams } from "react-router-dom"

import { SavedViewsResponses } from "@costory/types/endpoints/savedViews"
import { DATE_PRESETS } from "@costory/types/filters"
import { ChartType, DatePreset } from "@costory/types/prisma-client"

import { getDatePresetLabel } from "@costory/shared/utils/filters"

import { Chart } from "@costory/front/components/charts/Chart"
import { toReadableDate } from "@costory/front/utils/filters"

type Props = {
  title: string
  chartType: ChartType
  savedView: SavedViewsResponses.SavedView
  onDelete?: () => void
  height: number
  isEditable?: boolean
  onRename?: (val: string) => void
  isAnimationActive?: boolean
  isDashboardWidget?: boolean
}

const CARD_HEADER_HEIGHT = 100 // px

export const DashboardWidget = ({
  title,
  chartType,
  savedView,
  onDelete,
  onRename,
  height,
  isEditable = false,
  isAnimationActive = true,
  isDashboardWidget = false,
}: Props) => {
  const [isOpen, { open, close }] = useDisclosure()
  const [searchParams] = useSearchParams()

  const inputRef = useRef<HTMLInputElement>(null)
  const handleSave = () => {
    if (inputRef.current) {
      onRename?.(inputRef.current.value)
      close()
    }
  }

  // Create a modified saved view with date range if provided
  const modifiedSavedView = useMemo(() => {
    const from = searchParams.get("from")
    const to = searchParams.get("to")
    const datePreset = searchParams.get("datePreset")

    // If we have date range from URL, use it
    if (from && to) {
      // Validate that the datePreset is a valid DatePreset
      const validatedDatePreset =
        datePreset && DATE_PRESETS.includes(datePreset as DatePreset)
          ? (datePreset as DatePreset)
          : null

      return {
        ...savedView,
        from,
        to,
        datePreset: validatedDatePreset,
      }
    }

    return savedView
  }, [savedView, searchParams])

  // Create a modified title that reflects the date range
  const displayTitle = useMemo(() => {
    const datePreset = searchParams.get("datePreset")
    const from = searchParams.get("from")
    const to = searchParams.get("to")

    // If we have a preset, validate it and use its label
    if (datePreset && DATE_PRESETS.includes(datePreset as DatePreset)) {
      const presetLabel = getDatePresetLabel(datePreset)
      if (presetLabel) {
        // Replace any existing date range info in the title with the new preset
        return title.replace(
          /\b(Trailing\s+\d+\s+(Days?|Weeks?|Months?|Years?)|Last\s+(\d+\s+)?(Week|Month|Year|months?|years?)|This\s+(Week|Month|Year|Quarter)|Next\s+(Week|Month|Year|Quarter)|YTD|QTD|MTD|Last\s+Invoice\s+Month)\b/gi,
          presetLabel,
        )
      }
    }

    // If we have custom date range (from/to but no preset), format the dates
    if (from && to && !datePreset) {
      const customDateRange = `${toReadableDate(from)} - ${toReadableDate(to)}`
      // Replace any existing date range info in the title with the custom range
      return title.replace(
        /\b(Trailing\s+\d+\s+(Days?|Weeks?|Months?|Years?)|Last\s+(\d+\s+)?(Week|Month|Year|months?|years?)|This\s+(Week|Month|Year|Quarter)|Next\s+(Week|Month|Year|Quarter)|YTD|QTD|MTD|Last\s+Invoice\s+Month)\b/gi,
        customDateRange,
      )
    }

    return title
  }, [title, searchParams])

  // Create a time horizon label that reflects the date range if provided
  const timeHorizonLabel = useMemo(() => {
    const datePreset = searchParams.get("datePreset")
    const from = searchParams.get("from")
    const to = searchParams.get("to")

    // If we have a date preset, validate it and use its label
    if (datePreset && DATE_PRESETS.includes(datePreset as DatePreset)) {
      return getDatePresetLabel(datePreset) ?? ""
    }

    // If we have custom date range (from/to but no preset), format the dates
    if (from && to && !datePreset) {
      return `${toReadableDate(from)} - ${toReadableDate(to)}`
    }

    // Otherwise, use the original saved view's date preset
    return getDatePresetLabel(savedView.datePreset) ?? ""
  }, [savedView.datePreset, searchParams])
  return (
    <Stack
      h="100%"
      p={16}
      align={chartType === "DONUT" ? "center" : undefined}
      style={{
        overflow: chartType === "KPI_BREAKDOWN" ? "scroll" : "visible",
        scrollbarWidth: chartType === "KPI_BREAKDOWN" ? "none" : "auto",
      }}
    >
      <Group
        w="100%"
        justify="space-between"
        style={chartType === "DONUT" ? { minHeight: "40px" } : undefined}
      >
        <Group gap={3} wrap="nowrap" flex={1} align="baseline">
          {isEditable && (
            <ActionIcon
              size="sm"
              color="textMuted"
              className="dragHandle"
              styles={{ root: { cursor: "grab" } }}
            >
              <IconArrowsMove />
            </ActionIcon>
          )}
          <Tooltip label={displayTitle} multiline withinPortal={false} ml={7}>
            <Text fw="bold" size="md" lineClamp={2}>
              {chartType === "KPI" && displayTitle.length > 20
                ? `${displayTitle.substring(0, 20)}...`
                : displayTitle}
            </Text>
          </Tooltip>
          <Text size="sm" c="textSecondary" lineClamp={2}>
            {timeHorizonLabel}
          </Text>
        </Group>
        {isEditable ? (
          <Menu>
            <Menu.Target>
              <ActionIcon size="xs" color="textMuted">
                <IconDotsVertical />
              </ActionIcon>
            </Menu.Target>

            <Menu.Dropdown>
              <>
                <Menu.Item
                  leftSection={<IconPencil size={16} />}
                  onClick={open}
                >
                  Rename widget
                </Menu.Item>
                <Menu.Item
                  leftSection={<IconTrash size={16} />}
                  color="red"
                  onClick={onDelete}
                >
                  Delete widget
                </Menu.Item>
              </>
            </Menu.Dropdown>
          </Menu>
        ) : (
          <Tooltip label="Go to explorer">
            <ActionIcon
              component={Link}
              to={`/explorer/views/${savedView.id}`}
              size="xs"
            >
              <IconExternalLink />
            </ActionIcon>
          </Tooltip>
        )}
      </Group>
      <Chart
        chartType={chartType}
        filters={modifiedSavedView}
        height={height - CARD_HEADER_HEIGHT}
        withLegend={false}
        isAnimationActive={isAnimationActive}
        isDashboardWidget={isDashboardWidget}
      />
      <Modal
        opened={isOpen}
        onClose={close}
        title="Rename a widget"
        withinPortal
      >
        <Group>
          <Input
            flex={1}
            defaultValue={title}
            ref={inputRef}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleSave()
              }
            }}
          />
          <Button onClick={handleSave}>Save</Button>
        </Group>
      </Modal>
    </Stack>
  )
}
